import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Stethoscope,
  Calendar,
  FileText,
  Heart,
  Thermometer,
  Activity,
  Scale,
  Ruler,
  Phone,
  Clock,
  UserCheck,
} from "lucide-react";
import type { Constante } from "../../types";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import axiosInstance from "../../services/axiosInstance";

interface PatientInfo {
  dateOfBirth?: string;
  // Ajoutez d'autres champs si nécessaire
}

const ConstanteDetail: React.FC = () => {
  const { constanteId } = useParams<{ role: string; constanteId: string }>();
  const location = useLocation();
  const constante = location.state?.constante as Constante | undefined;
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [_loading, setLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);

  // Récupérer les informations du patient
  useEffect(() => {
    const fetchPatient = async () => {
      if (!constante?.patientId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axiosInstance.get(
          API_ENDPOINTS.PATIENTS.GET_BY_ID(constante.patientId)
        );
        setPatient({
          dateOfBirth: response.data.dateOfBirth,
          // Ajoutez d'autres champs si nécessaire
        });
      } catch (err) {
        console.error("Erreur lors du chargement du patient:", err);
        setError("Impossible de charger les informations du patient");
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [constante]);

  // Fonction pour retourner d'un pas en arrière dans l'historique
  const handleBackStep = () => {
    window.history.back();
  };

  // Retourne la valeur d'un champ en gérant plusieurs alias possibles
  const getFieldValue = (key: string) => {
    // Gérer date de naissance (déjà géré via customValue for patientBirthDate)
    if (key === "patientBirthDate") {
      return (
        patient?.dateOfBirth ||
        (constante as any).dateOfBirth ||
        (constante as any).dateNaissance ||
        null
      );
    }

    // Date de consultation peut se trouver sous plusieurs clés selon l'API
    if (key === "consultationDate") {
      return (
        (constante as any).consultationDate ||
        (constante as any).recordedAt ||
        (constante as any).dateVisit ||
        (constante as any).date ||
        null
      );
    }

    // Date de création courante
    if (key === "createdAt") {
      return (
        (constante as any).createdAt ||
        (constante as any).recordedAt ||
        (constante as any).updatedAt ||
        null
      );
    }

    // Par défaut, retourner la clé directement depuis l'objet constante
    return (constante as any)[key];
  };

  // Si aucune constante n'est passée via state, afficher un message d'erreur
  if (!constante) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-red-100 p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Données introuvables
              </h2>
              <p className="text-gray-600 mb-8 text-lg">
                Aucune donnée de constante trouvée pour l'ID {constanteId}.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleBackStep}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <ArrowLeft size={18} />
                  Retour
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fonction utilitaire pour formater les valeurs
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return "Non renseigné";
    if (value instanceof Date) return value.toLocaleString("fr-FR");
    if (typeof value === "object") {
      if (value.firstName || value.lastName) {
        return `${value.firstName || ""} ${value.lastName || ""}`.trim();
      }
      return "Non renseigné";
    }
    return String(value);
  };

  // Fonction pour formater la date de naissance
  const formatBirthDate = (dateString?: string): string => {
    if (!dateString) return "Non renseigné";

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("fr-FR", {
        year: "numeric",
        month: "long",
        day: "2-digit",
      }).format(date);
    } catch (error) {
      console.error("Erreur de formatage de la date:", error);
      return dateString;
    }
  };

  // Formate une date+heure ISO/Date en chaîne lisible en français (ex: 12 novembre 2025 à 13:03)
  const formatDateTime = (value: any): string => {
    if (!value) return "Non renseigné";
    const d = typeof value === "string" ? new Date(value) : value;
    if (d instanceof Date && !Number.isNaN(d.getTime())) {
      // format avec heure et minute
      const datePart = new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(d);
      const timePart = new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
      return `${datePart} à ${timePart}`;
    }
    return formatValue(value);
  };

  // Fonction pour obtenir l'icône appropriée
  const getIcon = (key: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      patientFullName: <User className="w-5 h-5" />,
      patientBirthDate: <Calendar className="w-5 h-5" />,
      doctorName: <Stethoscope className="w-5 h-5" />,
      doctor: <UserCheck className="w-5 h-5" />,
      patientPhone: <Phone className="w-5 h-5" />,
      weight: <Scale className="w-5 h-5" />,
      height: <Ruler className="w-5 h-5" />,
      bloodPressure: <Activity className="w-5 h-5" />,
      heartRate: <Heart className="w-5 h-5" />,
      temperature: <Thermometer className="w-5 h-5" />,
      reason: <FileText className="w-5 h-5" />,
      consultationDate: <Calendar className="w-5 h-5" />,
      createdAt: <Clock className="w-5 h-5" />,
      id: <FileText className="w-5 h-5" />,
    };
    return iconMap[key] || <FileText className="w-5 h-5" />;
  };

  // Fonction pour obtenir la couleur appropriée
  const getColor = (key: string) => {
    const colorMap: { [key: string]: string } = {
      patientFullName: "text-blue-600 bg-blue-50",
      patientBirthDate: "text-blue-600 bg-blue-50",
      doctorName: "text-green-600 bg-green-50",
      doctor: "text-green-600 bg-green-50",
      patientPhone: "text-purple-600 bg-purple-50",
      weight: "text-orange-600 bg-orange-50",
      height: "text-orange-600 bg-orange-50",
      bloodPressure: "text-red-600 bg-red-50",
      heartRate: "text-pink-600 bg-pink-50",
      temperature: "text-yellow-600 bg-yellow-50",
      reason: "text-indigo-600 bg-indigo-50",
      consultationDate: "text-teal-600 bg-teal-50",
      createdAt: "text-gray-600 bg-gray-50",
      id: "text-slate-600 bg-slate-50",
    };
    return colorMap[key] || "text-gray-600 bg-gray-50";
  };

  // Organisation des champs par sections
  const sections = [
    {
      title: "Informations Patient",
      icon: <User className="w-6 h-6" />,
      color: "text-blue-600 bg-blue-50 border-blue-200",
      fields: [
        { label: "Nom complet", key: "patientFullName" },
        {
          label: "Date de naissance",
          key: "patientBirthDate",
          customValue: formatBirthDate(patient?.dateOfBirth),
          customIcon: <Calendar className="w-5 h-5" />,
        },
        { label: "Téléphone", key: "patientPhone" },
      ],
    },
    {
      title: "Informations Médicales",
      icon: <Stethoscope className="w-6 h-6" />,
      color: "text-green-600 bg-green-50 border-green-200",
      fields: [
        { label: "Médecin", key: "doctorName" },
        { label: "Motif de consultation", key: "reason" },
      ],
    },
    {
      title: "Constantes Vitales",
      icon: <Activity className="w-6 h-6" />,
      color: "text-red-600 bg-red-50 border-red-200",
      fields: [
        { label: "Poids", key: "weight" },
        { label: "Taille", key: "height" },
        { label: "Tension artérielle", key: "bloodPressure" },
        { label: "Rythme cardiaque", key: "heartRate" },
        { label: "Température", key: "temperature" },
      ],
    },
    {
      title: "Informations de Consultation",
      icon: <Calendar className="w-6 h-6" />,
      color: "text-purple-600 bg-purple-50 border-purple-200",
      fields: [
        { label: "Date de consultation", key: "consultationDate" },
        { label: "Date de création", key: "createdAt" },
        { label: "ID de la constante", key: "id" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header avec navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Constante #{constante.id}
                </h1>
                <p className="text-gray-600 mt-1">
                  Détails complets de la consultation médicale
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBackStep}
                className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-1"
              >
                <ArrowLeft size={18} />
                Retour
              </button>
            </div>
          </div>
        </div>

        {/* Contenu principal organisé en sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="space-y-6">
              <div
                className={`bg-white rounded-2xl shadow-lg border ${section.color} p-6 transform hover:scale-105 transition-all duration-300`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-3 rounded-xl ${section.color}`}>
                    {section.icon}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-4">
                  {section.fields.map((field) => (
                    <div key={field.key} className="group">
                      <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-white border-2 border-transparent hover:border-gray-200 transition-all duration-200">
                        <div
                          className={`p-2 rounded-lg ${getColor(
                            field.key
                          )} group-hover:scale-110 transition-transform duration-200`}
                        >
                          {"customIcon" in field
                            ? field.customIcon
                            : getIcon(field.key)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {field.label}
                          </h3>
                          <p className="text-gray-600 break-words">
                            {"customValue" in field
                              ? field.customValue
                              : ["consultationDate", "createdAt"].includes(
                                  field.key
                                )
                              ? formatDateTime(getFieldValue(field.key))
                              : formatValue(getFieldValue(field.key))}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConstanteDetail;
